import { inject, Injectable, Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { forkJoin, Observable, take } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ApiUserResponse } from '../../api/authorisation/models';
import { UsersService } from '../../api/authorisation/services';
import { filterDefined } from '../../functions/rxjs-pipes/filter-defined';
import { uniqueByKeys } from '../../functions/utilities/array-utility';
import { isServiceProviderSupportUser, isSuperUserOrDeveloper } from '../../services/usercapabilities.service';
import { RouterFacadeService } from '../router/router-facade.service';
import { UserStore } from './user.store';
import { User } from './user.types';

@Injectable({ providedIn: 'root' })
export class UserFacadeService {
  private _usersService = inject(UsersService);
  private _userStore = inject(UserStore);
  private _routerFacadeService = inject(RouterFacadeService);

  isReadOnly: Signal<boolean> = this._userStore.isReadOnly;

  private _boardId: Signal<string> = this._routerFacadeService.boardId;
  private _tenantId: Signal<string> = this._routerFacadeService.tenantId;

  user$: Observable<User> = toObservable(this._userStore.user).pipe(
    tap((user) => {
      const isUserLoaded = !!user;
      const isUserLoading = this._userStore.isUserLoading();
      if (!isUserLoaded && !isUserLoading) {
        this._userStore.loadUser();
      }
    }),
    filterDefined,
    take(1)
  );

  clearUser() {
    this._userStore.clearUser();
  }

  watchlistUsers$(): Observable<ApiUserResponse[]> {
    return forkJoin([
      this._usersService.listTenantWatchlistUsers({ tenantId: this._tenantId() }),
      this._usersService.listBoardWatchlistUsers({ boardId: this._boardId(), tenantId: this._tenantId() }),
    ]).pipe(map(([twu, bwu]) => [...(twu.users ?? []), ...(bwu.users ?? [])].filter(uniqueByKeys(['id']))));
  }

  getUserPermissions(): {
    canManageBoardUsers: boolean;
    canManageTenantUsers: boolean;
    isBoardOwner: boolean;
    isTenantOwner: boolean;
    isStaff: boolean;
  } {
    const boardId = this._boardId();
    const tenantId = this._tenantId();
    const user = this._userStore.user();

    const currentTenant = user?.tenants?.find((tenant) => tenant.id === tenantId)! ?? null;
    const currentBoard = currentTenant?.boards?.find((board) => board.id === boardId)! ?? null;

    const hasTenantGrant = !!currentTenant?.role;
    const hasBoardGrant = !!currentBoard?.role;
    const isTenantOwner = currentTenant?.role === 'TENANT_OWNER';
    const isTenantMember = currentTenant?.role === 'TENANT_MEMBER';
    const isBoardOwner = currentBoard?.role === 'BOARD_OWNER';
    const isSuperUser = !!user && isSuperUserOrDeveloper(user?.globalRoles);
    const isStaff = isSuperUser || (!!user && isServiceProviderSupportUser(user));

    const canManageBoardUsers =
      isSuperUser || isBoardOwner || isTenantOwner || (isStaff && !hasTenantGrant && !hasBoardGrant);
    const canManageTenantUsers = isSuperUser || isTenantOwner || (isStaff && !isTenantMember);

    return {
      canManageBoardUsers,
      canManageTenantUsers,
      isBoardOwner,
      isTenantOwner,
      isStaff,
    };
  }

  reloadUser() {
    this._userStore.loadUser(true);
  }
}
